import React, { useContext, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { SettingsContextProps } from '../Contexts/SettingsContext';
import SettingsContext from '../Contexts/SettingsContext';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import type { ModeContextProps } from '../Contexts/ModeContext';
import ModeContext from '../Contexts/ModeContext';
import type { SystemContextProps } from '../Contexts/SystemContext';
import SystemContext from '../Contexts/SystemContext';
import type { ApiContextProps } from '../Contexts/ApiContext';
import ApiContext from '../Contexts/ApiContext';
import type { TokenContextProps } from '../Contexts/TokenContext';
import TokenContext from '../Contexts/TokenContext';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import AppTheme from '../Enums/AppTheme';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CurrentUser from '../Models/CurrentUserModel';
import Mode from '../Enums/Mode';
import { updateLastActivity } from '../Utils/LastActivity';
import type { IndexedDBContextProps } from '../Contexts/IndexedDBContext';
import IndexedDBContext from '../Contexts/IndexedDBContext';
import TokenType from '../Enums/TokenType';
import type KFleetToken from '../Models/KFleetTokenModel';
import type TenantToken from '../Models/TenantTokenModel';
import ApiService from '../Services/ApiService';
import { handleDisconnect } from '../Utils/HandleDisconnect';
import { sha256 } from 'shouldve-been-js';
import { useTitle, shakeForm } from 'shouldve-been-react';
import { PasswordInput, LoadingButton, PinCodeInput, ErrorMessage, ConfirmDialog } from 'shouldve-been-mui';

type ReLoginProps = {
    refreshTokenExpired: boolean,
};

function ReLoginPage(props: Readonly<ReLoginProps>): JSX.Element {

    useTitle('K-Fleet Experience - Login');

    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);

    const currentTheme: Theme = useTheme();

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);

    const [password, setPassword] = useState<string>('');
    const [showConfirmDisconnectDialog, setShowConfirmDisconnectDialog] = useState<boolean>(false);
    const [showConfirmRefreshTokenExpiredPinDialog, setShowConfirmRefreshTokenExpiredPinDialog] = useState<boolean>(false);
    const [pending, setPending] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [preferPassword, setPreferPassword] = useState<boolean>(props.refreshTokenExpired);
    const [prevRefreshTokenExpiredValue, setPrevRefreshTokenExpiredValue] = useState<boolean>(props.refreshTokenExpired);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => { //updates relogin method when prop changes

        if (props.refreshTokenExpired !== prevRefreshTokenExpiredValue) {
            setPreferPassword(props.refreshTokenExpired);
            setPrevRefreshTokenExpiredValue(props.refreshTokenExpired);
        }
    }, [prevRefreshTokenExpiredValue, props.refreshTokenExpired]);

    function updateTheme(theme: AppTheme): void {

        settingsContext.setTheme(theme);
        localStorageContext.localStorageService.setTheme(theme);
    }

    function handleKeyboardPasswordSubmit(e: React.KeyboardEvent): void {

        if (e.key === 'Enter') {
            e.preventDefault();
            void handlePasswordSubmit();
        }
    }

    async function handlePinSubmit(pin: string): Promise<void> {

        const hashedPin: string | null = await sha256((userContext.currentUser?.username ?? '') + '_' + pin);

        if (hashedPin === userContext.hashedPin) {

            updateLastActivity(userContext, localStorageContext);
            modeContext.setMode(Mode.MAIN);
            localStorageContext.localStorageService.setMode(Mode.MAIN);

        } else {

            setErrorMessage('Wrong PIN');
            shakeForm(formRef);
        }
    }

    async function handlePasswordSubmit(): Promise<void> {

        setPending(true);

        if (tokenContext.tokenType === TokenType.TENANT) {

            const tokenInfo: TenantToken | null = await ApiService.fetchNewTenantTokenByEmailAndPassword(userContext.currentUser?.email ?? '', password, systemContext.tenantDisplayName ?? '') ?? null;

            const token: string | null = tokenInfo?.token ?? null;
            const refreshToken: string | null = tokenInfo?.refreshToken ?? null;
            const expiration: Date | null = tokenInfo?.expiration ?? null;

            if (token === null || refreshToken === null || expiration === null) {

                setPending(false);
                setErrorMessage('Could not login');
                
                shakeForm(formRef);

                return;
            }

            const access: string[] = await apiContext.api?.getUserAccess(userContext.currentUser?.userId ?? '', token) ?? [];

            if (access.length === 0) {

                setPending(false);
                setErrorMessage('Could not login');
                
                shakeForm(formRef);

                return;
            }

            const currentUser: CurrentUser = new CurrentUser(userContext.currentUser?.userId ?? '', userContext.currentUser?.username ?? '', userContext.currentUser?.email ?? '', access);

            userContext.setCurrentUser(currentUser);
            localStorageContext.localStorageService.setCurrentUser(currentUser);
            updateLastActivity(userContext, localStorageContext);
            
            modeContext.setMode(Mode.MAIN);
            localStorageContext.localStorageService.setMode(Mode.MAIN);

            tokenContext.setToken(token);
            tokenContext.setRefreshToken(refreshToken);
            tokenContext.setExpiration(expiration);
            localStorageContext.localStorageService.setToken(token);
            localStorageContext.localStorageService.setRefreshToken(refreshToken);
            localStorageContext.localStorageService.setExpiration(expiration);
        } else {

            const tokenInfo: KFleetToken | null = await apiContext.api?.fetchNewKFleetTokenByUsernameAndPassword(userContext.currentUser?.username ?? '', password) ?? null;

            const token: string | null = tokenInfo?.token ?? null;
            const refreshToken: string | null = tokenInfo?.refreshToken ?? null;
            const userId: string | null = tokenInfo?.userId ?? null;
            const access: string[] | null = tokenInfo?.access ?? null;
            const expiration: Date | null = tokenInfo?.expiration ?? null;

            if (token === null || refreshToken === null || userId === null || access === null || expiration === null) {

                setPending(false);
                setErrorMessage('Could not login');
                
                shakeForm(formRef);

                return;
            }

            const currentUser: CurrentUser = new CurrentUser(userId, userContext.currentUser?.username ?? '', userContext.currentUser?.email ?? '', access);

            userContext.setCurrentUser(currentUser);
            localStorageContext.localStorageService.setCurrentUser(currentUser);
            updateLastActivity(userContext, localStorageContext);
            
            modeContext.setMode(Mode.MAIN);
            localStorageContext.localStorageService.setMode(Mode.MAIN);

            tokenContext.setToken(token);
            tokenContext.setRefreshToken(refreshToken);
            tokenContext.setExpiration(expiration);
            localStorageContext.localStorageService.setToken(token);
            localStorageContext.localStorageService.setRefreshToken(refreshToken);
            localStorageContext.localStorageService.setExpiration(expiration);
        }

        setPending(false);
    }

    function handleCheckboxToggle(e: React.ChangeEvent<HTMLInputElement>): void {
        
        if (props.refreshTokenExpired && !e.target.checked) {
            setShowConfirmRefreshTokenExpiredPinDialog(true);
        } else {
            setPreferPassword(e.target.checked);
        }
    } 

    return (
        <div style={{
            padding: '5px',
            height: windowSize[1],
            overflow: 'auto',
        }}>
            <div style={{
                textAlign: 'right',
                padding: '10px 10px 0px 0px',
            }}>
                <table style={{
                    marginLeft: 'auto',
                    marginTop: '10px',
                }}>
                    <tbody>
                        <tr>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                            }}>
                                Dark Mode
                            </td>
                            <td>
                                <FormControl>
                                    <Switch
                                        checked={settingsContext.theme === AppTheme.DARK_MODE}
                                        onChange={(e: React.ChangeEvent): void => updateTheme((e.target as HTMLInputElement).checked ? AppTheme.DARK_MODE : AppTheme.LIGHT_MODE)}
                                        color='primary'
                                    />           
                                </FormControl>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div 
                style={{
                    textAlign: 'center',
                    marginTop: '20px',
                }}
            >
                <img 
                    src={systemContext.logoUrl ?? ''}
                    alt='Logo' 
                    width='80px'
                    style={{
                        marginBottom: '20px',
                    }}
                />
                <div style={{
                    marginBottom: '20px',
                    fontSize: '20px',
                    fontWeight: 500,
                    lineHeight: '170%',
                }}>
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 500,
                        color: currentTheme.palette.text.primary,
                    }}>
                        Connected to:
                    </span>
                    <br />
                    <span style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: currentTheme.palette.text.primary,
                    }}>
                        {systemContext.systemName ?? 'ERROR'}
                    </span>
                </div>
                <div style={{
                    marginBottom: '20px',
                    fontSize: '20px',
                    fontWeight: 500,
                    lineHeight: '170%',
                }}>
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 500,
                        color: currentTheme.palette.text.primary,
                    }}>
                        Current User:
                    </span>
                    <br />
                    <span style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: currentTheme.palette.text.primary,
                    }}>
                        {userContext.currentUser?.username ?? 'ERROR'}
                    </span>
                </div>
                {preferPassword 
                    ? (
                        <form noValidate autoComplete='off' ref={formRef} name='relogin'>
                            <div style={{
                                marginBottom: '10px',
                            }}>
                                <PasswordInput 
                                    password={password} 
                                    onPasswordChange={setPassword}
                                    onKeyDown={handleKeyboardPasswordSubmit}
                                    minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                    maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                                />
                            </div>
                            <br />
                            <div>
                                <LoadingButton 
                                    width='150px'
                                    height='40px'
                                    text='Login'
                                    controller={pending}
                                    onClick={handlePasswordSubmit}
                                    buttonColor='primary'
                                    circularColor='error'
                                    circularSize='30px'
                                    variant='contained'
                                />
                                <br />
                            </div>
                        </form>
                    )
                    : (
                        <PinCodeInput
                            length={4}
                            onComplete={handlePinSubmit}
                        />
                    )
                }
                <br />
                <FormControlLabel 
                    control={
                        <Checkbox
                            color='primary'
                            checked={preferPassword}
                            onChange={handleCheckboxToggle}
                        />
                    }
                    label={
                        <span style={{
                            color: currentTheme.palette.text.primary,
                        }}>
                            Use Password instead of PIN
                        </span>
                    }
                />
                <br />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                />
                <br />
                <Button 
                    variant='contained'
                    color='error'
                    onClick={(): void => setShowConfirmDisconnectDialog(true)}
                    style={{
                        minWidth: '150px',
                        maxWidth: '150px',
                        minHeight: '40px',
                        maxHeight: '40px',
                    }}
                >
                    Disconnect
                </Button>
            </div>

            <ConfirmDialog 
                controller={showConfirmDisconnectDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmDisconnectDialog(false);
                    if (confirm) {
                        await handleDisconnect(apiContext, modeContext, localStorageContext, indexedDBContext, userContext, tokenContext, systemContext);
                    }
                }}
                title='All Unsynced Data will be Lost'
                text='If you have unsynced data, it will be deleted when disconnecting. Are you sure you want to continue?'
                noText='No'
                yesText='Yes'
                loadingConfirm={false}
            />

            <ConfirmDialog 
                controller={showConfirmRefreshTokenExpiredPinDialog}
                // eslint-disable-next-line @typescript-eslint/require-await
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmRefreshTokenExpiredPinDialog(false);
                    if (confirm) {
                        setPreferPassword(false);
                    }
                }}
                title='Your Session has Expired'
                text='You need to login with your password to download and upload new data. This requires a connection to your system. If you login with your PIN, you can only work offline. Are you sure you want to use PIN?'
                noText='No'
                yesText='Yes'
                loadingConfirm={false}
            />
        </div>
    );
}

export default ReLoginPage;